import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { NetworkManager } from './services';
import ProtectedRoutes from './pages/Login/ProtectedRoutes';
import { useTokenMessageHandler } from './hooks/useTokenMessageHandler';
import Deals from './pages/Deals/Deals';
import Layout from './components/organisms/Layout/Layout';
import DealDetail from './pages/DealDetail/DealDetail';
import Onboarding from './pages/Onboarding/Onboarding';
import { Toaster } from 'react-hot-toast';
import Clients from './pages/Clients/Clients';
import ClientDetail from './pages/ClientDetail/ClientDetail';
import Investments from './pages/Investments/Investments';
import Home from './pages/Home/Home';
import Terms from './pages/Terms/Terms';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import RiskDisclosure from './pages/RiskDisclosure/RiskDisclosure';
import Profile from './pages/Profile/Profile';
import ProductDeck from './pages/ProductDeck/ProductDeck';
import TapAcademy from './pages/TapAcademy/TapAcademy';
import UserPayment from './pages/UserPayment/UserPayment';
import BondsDealDetail from './pages/BondsDealDetail/BondsDealDetail';
import { BONDS_DEALS_ENABLED } from './flags';
import Partners from './pages/Partners/Partners';
import PartnersDetail from './pages/partnersDetail/PartnersDetail';
import FamilyAccount from './pages/familyAccount/FamilyAccount';
import FamilyDetails from './pages/FamilyDetail/FamilyDetails';
import Faq from './components/organisms/FAQPage/Faq';
import { useProfile } from './context/ProfileContext';
import GlobalLoadingPage from './components/LoadingPage/GlobalLoadingPage';
import BondsPTCDealDetail from './pages/BondsDealDetail/BondsPTCDealDetail';
import ListedBondDetail from './pages/DealDetail/ListedBonds/ListedBondDetail';
import UploadStatus from './components/organisms/UploadStatus';
import CommissionTable from './pages/CommissionTable/CommissionTable';
import ClientPayouts from './pages/ClientPayouts/ClientPayouts';
import { toast } from 'react-hot-toast';
import MarketingCreatives from './pages/MarketingCreatives/MarketingCreatives';
import MarketingCreativeSection from './pages/MarketingCreatives/MarketingCreativeSection';
import Agreement from './pages/Agreement/Agreement';
import RequireAgreementSigned from './components/organisms/AgreementGate/RequireAgreementSigned';

async function validateTokenAndFetchProfile(
  navigate,
  setIsNotReady,
  updateProfileData,
  validateToken = true,
) {
  const token = localStorage.getItem('token');
  // If no token is available, clear loading and exit.
  if (!token) {
    setIsNotReady(false);
    return;
  }
  const currentPath = window.location.pathname;
  const fullPath = window.location.pathname + window.location.search;
  setIsNotReady(true);

  try {
    if (validateToken) {
      const validationResponse = await NetworkManager.Call({
        method: 'POST',
        path: 'onboarding/validate/token',
        data: token,
        headers: {
          'Content-Type': 'text/plain',
        },
      });

      if (!validationResponse) {
        // Token is invalid or expired
        localStorage.removeItem('token'); // Clear the invalid token
        toast.dismiss(); // Dismiss any existing toasts
        toast.error('Your session has expired. Please login again.', {
          duration: 5000,
          onClose: () => {
            setIsNotReady(false);
            navigate('/login');
          },
        });
        return;
      }
    }
    const profileResponse = await NetworkManager.Call({
      method: 'GET',
      path: 'profile/v1/get-partner-config',
      retry: 3,
    });
    const profileData = profileResponse?.data;
    if (profileData) {
      updateProfileData(profileData);
      // Redirect based on profile data
      if (profileData?.type === 'PARTNER' && ['/', '/signup', '/login'].includes(currentPath)) {
        if (currentPath !== '/deals') {
          navigate('/deals');
        }
      } else {
        if (currentPath !== fullPath) {
          navigate(fullPath);
        }
      }
    } else {
      navigate(fullPath);
    }
  } catch (error) {
    console.error(error?.response?.data?.message || 'An error occurred');
    if (error?.response?.status === 401) {
      // Handle unauthorized/expired token
      localStorage.removeItem('token');
      toast.dismiss(); // Dismiss any existing toasts
      toast.error('Your session has expired. Please login again.', {
        duration: 5000,
        onClose: () => {
          setIsNotReady(false);
          navigate('/login');
        },
      });
    } else {
      navigate(fullPath);
    }
  } finally {
    setIsNotReady(false);
  }
}
function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNotReady, setIsNotReady] = useState(true);
  const { updateProfileData, profileData } = useProfile();
  const [bulkUploadTriggered, setBulkUploadTriggered] = useState(false);
  const excludedRoutes = ['/login', '/'];
  const shouldShowUploadStatus = !excludedRoutes.includes(location.pathname);
  const [validatedToken, setValidatedToken] = useState(null);

  // Callback to handle token received via postMessage
  const onTokenReceived = useCallback(
    async (token, pt) => {
      if (token && token !== validatedToken) {
        localStorage.setItem('token', token);
        // Wait briefly to ensure localStorage is updated.
        await new Promise((resolve) => setTimeout(resolve, 100));
        await validateTokenAndFetchProfile(navigate, setIsNotReady, updateProfileData);
        setValidatedToken(token);
      }
    },
    [navigate, updateProfileData, validatedToken],
  );

  useTokenMessageHandler(onTokenReceived);

  // Listen for storage changes (optional fallback)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'token' && event.newValue) {
        onTokenReceived(event.newValue);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [onTokenReceived]);

  // Send the "READY" message to the parent when the child loads.
  useLayoutEffect(() => {
    const target = window.opener || window.parent;
    if (target && typeof target.postMessage === 'function') {
      target.postMessage({ type: 'READY' }, '*');
    }
  }, []);

  useEffect(() => {
    if (
      profileData?.type === 'PARTNER' &&
      (location.pathname === '/login' || location.pathname === '/signup')
    ) {
      navigate('/deals');
    }
  }, [profileData, location.pathname, navigate]);
  // Fetch profile on mount if token exists and hasn't been validated yet.
  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem('token');
      if (token && token !== validatedToken) {
        await validateTokenAndFetchProfile(navigate, setIsNotReady, updateProfileData);
        setValidatedToken(token);
      } else {
        setIsNotReady(false);
      }
    };
    fetchProfile();
  }, [navigate, updateProfileData, validatedToken]);
  if (isNotReady) {
    return <GlobalLoadingPage />;
  }
  return (
    <div>
      <NetworkManager.CustomQueryClientProvider>
        <Toaster />
        {shouldShowUploadStatus && (
          <UploadStatus
            bulkUploadTriggered={bulkUploadTriggered}
            setBulkUploadTriggered={setBulkUploadTriggered}
          />
        )}
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/risk-disclosure" element={<RiskDisclosure />} />
          <Route path="/faq" element={<Faq />} />
          <Route element={<ProtectedRoutes />}>
            <Route path="*" element={<Navigate to="/deals" replace={true} />} />
            <Route element={<Layout />}>
              {/* Not available until the partner agreement is e-signed */}
              <Route element={<RequireAgreementSigned />}>
                {profileData?.features?.enableClient && (
                  <Route
                    path="/clients"
                    element={<Clients setBulkUploadTriggered={setBulkUploadTriggered} />}
                  />
                )}
                {profileData?.features?.enableMasterPartner && (
                  <Route
                    path="/partners"
                    element={<Partners setBulkUploadTriggered={setBulkUploadTriggered} />}
                  />
                )}
                {profileData?.features?.enableClient && (
                  <Route path="/clients/:clientId" element={<ClientDetail />} />
                )}
                {profileData?.features?.enableFamilyAccount && (
                  <Route path="/family-account" element={<FamilyAccount />} />
                )}
                {profileData?.features?.enableFamilyAccount && (
                  <Route path="/family-account/:familyAccountId" element={<FamilyDetails />} />
                )}
                {profileData?.features?.enableMasterPartner && (
                  <Route path="/partners/:partnerId" element={<PartnersDetail />} />
                )}
                <Route path="/deals/:dealId" element={<DealDetail />} />
                {BONDS_DEALS_ENABLED && (
                  <Route path="/bond-deals/:dealId" element={<BondsDealDetail />} />
                )}
                {BONDS_DEALS_ENABLED && (
                  <Route path="/bond-deals/PTC/:dealId" element={<BondsPTCDealDetail />} />
                )}
                <Route path="/deals/:dealId/:financeType" element={<DealDetail />} />
                <Route path="/listed-bonds/:isin/:id" element={<ListedBondDetail />} />
                <Route path="/investments" element={<Investments />} />
                {profileData?.features?.enableClientPayouts && (
                  <Route path="/client-payouts" element={<ClientPayouts />} />
                )}
              </Route>
              {profileData?.clientActions?.showEarnedIncome && (
                <Route path="/commission-table" element={<CommissionTable />} />
              )}
              {profileData?.features?.enableProfile && (
                <Route path="/profile" element={<Profile />} />
              )}
              <Route path="/deals" element={<Deals />} />
              <Route path="/agreement" element={<Agreement />} />
              {profileData?.features?.enableTapAcademy && (
                <Route path="/tap-academy" element={<TapAcademy />} />
              )}
              {profileData?.features?.enableProductResources && (
                <Route path="/product-resources" element={<ProductDeck />} />
              )}{' '}
              {profileData?.features?.enableMarketingCreative && (
                <Route path="/marketing-creatives" element={<MarketingCreatives />} />
              )}
              {profileData?.features?.enableMarketingCreative && (
                <Route
                  path="/marketing-creatives/:sectionId"
                  element={<MarketingCreativeSection />}
                />
              )}
            </Route>
          </Route>
          <Route path="/login" element={<Onboarding />} />
          <Route path="/user-payment/:productType" element={<UserPayment />} />
        </Routes>
      </NetworkManager.CustomQueryClientProvider>
    </div>
  );
}
export default App;
